// layouts/catalogos/generos/editar-genero.js

import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";

import FormPage from "ui/FormPage";
import { PrimaryButton, OutlineButton } from "ui/Buttons";
import FormTextField from "ui/FormTextField";

import { getGeneroById, updateGenero } from "../services/generos";

const fmtDate = (d) => {
  if (!d) return "";
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return d;
    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yyyy = dt.getFullYear();
    const hh = String(dt.getHours()).padStart(2, "0");
    const mi = String(dt.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
  } catch {
    return d;
  }
};

/* -------- helper de errores (igual estilo que en otros módulos) -------- */
function parseApiErrors(err) {
  const out = [];
  const res = err?.response;
  const d = res?.data ?? err;

  const push = (m) => {
    if (m == null) return;

    if (typeof m === "string") { out.push(m); return; }
    if (typeof m === "number" || typeof m === "boolean") { out.push(String(m)); return; }

    if (Array.isArray(m)) { m.forEach(push); return; }

    if (typeof m === "object") {
      if (Array.isArray(m.errors))  m.errors.forEach(push);
      if (Array.isArray(m.message)) m.message.forEach(push);
      if (Array.isArray(m.details)) m.details.forEach(push);
      if (Array.isArray(m.issues))  m.issues.forEach(push);

      if (typeof m.message === "string") out.push(m.message);
      if (typeof m.msg === "string")      out.push(m.msg);
      if (typeof m.error === "string")    out.push(m.error);

      if (m.instancePath || m.path || m.dataPath) {
        const path = String(m.instancePath || m.path || m.dataPath || "")
          .replace(/^\//, "").replace(/\//g, ".");
        const msg = m.message || m.msg || "error";
        out.push(path ? `${path}: ${msg}` : msg);
      }

      if (out.length === 0) {
        try { out.push(JSON.stringify(m)); } catch { out.push(String(m)); }
      }
      return;
    }

    out.push(String(m));
  };

  push(d);
  if (res?.statusText && res.statusText !== "OK") out.push(res.statusText);
  if (res?.status) out.push(`HTTP ${res.status}`);

  if (out.length === 0) out.push("Error del servidor");
  return [...new Set(out)].filter(Boolean);
}
/* ----------------------------------------------------------------------- */

export default function EditarGenero() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  const [name, setName] = useState("");
  const [createdAt, setCreatedAt] = useState("");

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setErrors([]);
      try {
        const g = await getGeneroById(id);
        if (cancel) return;
        setName(g?.name ?? g?.nombre ?? "");
        setCreatedAt(g?.created_at ?? g?.createdAt ?? "");
      } catch (e) {
        if (!cancel) {
          const errs = parseApiErrors(e);
          setErrors(Array.isArray(errs) ? errs : [String(errs || "Error")]);
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [id]);

  const isValid = useMemo(() => !!name.trim(), [name]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;
    setSaving(true);
    setErrors([]);
    try {
      await updateGenero(id, { name: name.trim() }); // 👈 solo nombre
      navigate("/catalogos/generos", { replace: true });
    } catch (e2) {
      const errs = parseApiErrors(e2);
      setErrors(Array.isArray(errs) ? errs : [String(errs || "Error")]);
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormPage
      title="Editar género"
      onBack={() => navigate("/catalogos/generos")}
      pageMaxWidth={1100}
      contentMaxWidth={900}
      actions={
        <>
          <PrimaryButton
            type="submit"
            form="form-genero"
            disabled={!isValid || saving || loading}
          >
            {saving ? "Guardando..." : "Guardar"}
          </PrimaryButton>
          <OutlineButton onClick={() => navigate("/catalogos/generos")}>
            Cancelar
          </OutlineButton>
        </>
      }
    >
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.length === 1 ? (
            errors[0]
          ) : (
            <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
              {errors.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          )}
        </Alert>
      )}

      <form id="form-genero" onSubmit={onSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormTextField
              label="Nombre*"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormTextField
              label="Creado"
              value={fmtDate(createdAt)}
              disabled
            />
          </Grid>
        </Grid>
      </form>
    </FormPage>
  );
}
